import "@/components/workspace/workspace.css";
import {
	type MenuBarItem,
	Workspace,
	type WorkspaceConfig,
	useWorkspaceContext,
} from "@/components/workspace";
import { ModuleErrorBoundary } from "@/components/ModuleErrorBoundary";
import {
	Outlet,
	createFileRoute,
	useNavigate,
} from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	useCreateDemandSignal,
	useCheckCtp,
	useTriggerReplan,
	useSimulate,
} from "@/hooks/use-manufacturing";
import { ProductPicker } from "@/components/manufacturing/ProductPicker";
import { useManufacturingContext } from "@/lib/manufacturing-context";
import { toast } from "sonner";

export const Route = createFileRoute("/manufacturing")({
	component: ManufacturingLayout,
});

// ── New Demand Signal Dialog ──────────────────────────────

function NewDemandDialog({
	open,
	onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
	const { t } = useTranslation("manufacturing");
	const create = useCreateDemandSignal();
	const [productId, setProductId] = useState("");
	const [qty, setQty] = useState("");
	const [needDate, setNeedDate] = useState("");
	const [priority, setPriority] = useState("high");

	const handleSubmit = useCallback(() => {
		if (!productId || !qty || !needDate) return;
		create.mutate(
			{
				productId,
				qty: Number(qty),
				uom: "шт",
				needDate,
				probability: 1.0,
				priority,
				source: "manual_planning",
			},
			{
				onSuccess: () => {
					toast.success("Сигнал спроса создан");
					onOpenChange(false);
					setProductId("");
					setQty("");
					setNeedDate("");
				},
			},
		);
	}, [productId, qty, needDate, priority, create, onOpenChange]);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[420px]">
				<DialogHeader>
					<DialogTitle>{t("menu_new_demand")}</DialogTitle>
				</DialogHeader>
				<div className="grid gap-3 py-2">
					<div className="grid gap-1.5">
						<Label className="text-xs">{t("field_product")}</Label>
						<ProductPicker
							value={productId}
							onChange={setProductId}
							placeholder="Выберите товар"
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="grid gap-1.5">
							<Label className="text-xs">{t("field_qty")}</Label>
							<Input
								type="number"
								value={qty}
								onChange={(e) => setQty(e.target.value)}
								placeholder="100"
								className="h-8 text-xs"
							/>
						</div>
						<div className="grid gap-1.5">
							<Label className="text-xs">{t("field_need_date")}</Label>
							<Input
								type="date"
								value={needDate}
								onChange={(e) => setNeedDate(e.target.value)}
								className="h-8 text-xs"
							/>
						</div>
					</div>
					<div className="grid gap-1.5">
						<Label className="text-xs">{t("field_priority")}</Label>
						<Select value={priority} onValueChange={setPriority}>
							<SelectTrigger className="h-8 text-xs">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="critical">{t("urgency_critical")}</SelectItem>
								<SelectItem value="high">{t("urgency_high")}</SelectItem>
								<SelectItem value="medium">{t("urgency_normal")}</SelectItem>
								<SelectItem value="low">{t("urgency_low")}</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</div>
				<div className="flex justify-end gap-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => onOpenChange(false)}
					>
						Отмена
					</Button>
					<Button
						size="sm"
						onClick={handleSubmit}
						disabled={!productId || !qty || !needDate || create.isPending}
					>
						{create.isPending ? "Создание..." : "Создать"}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}

// ── CTP Dialog ────────────────────────────────────────────

function CtpDialog({
	open,
	onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
	const { t } = useTranslation("manufacturing");
	const checkCtp = useCheckCtp();
	const [productId, setProductId] = useState("");
	const [qty, setQty] = useState("");
	const [requestedDate, setRequestedDate] = useState("");

	const handleCheck = useCallback(() => {
		if (!productId || !qty) return;
		checkCtp.mutate({
			productId,
			qty: Number(qty),
			requestedDate,
			priority: "high",
		});
	}, [productId, qty, requestedDate, checkCtp]);

	const result = checkCtp.data;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-[480px]">
				<DialogHeader>
					<DialogTitle>{t("ctp_title")}</DialogTitle>
				</DialogHeader>
				<div className="grid gap-3 py-2">
					<div className="grid gap-1.5">
						<Label className="text-xs">{t("field_product")}</Label>
						<ProductPicker
							value={productId}
							onChange={setProductId}
							placeholder="Выберите товар"
						/>
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div className="grid gap-1.5">
							<Label className="text-xs">{t("field_qty")}</Label>
							<Input
								type="number"
								value={qty}
								onChange={(e) => setQty(e.target.value)}
								className="h-8 text-xs"
							/>
						</div>
						<div className="grid gap-1.5">
							<Label className="text-xs">{t("field_target_date")}</Label>
							<Input
								type="date"
								value={requestedDate}
								onChange={(e) => setRequestedDate(e.target.value)}
								className="h-8 text-xs"
							/>
						</div>
					</div>
					<Button
						size="sm"
						onClick={handleCheck}
						disabled={!productId || !qty || checkCtp.isPending}
						className="w-full"
					>
						{checkCtp.isPending ? "Проверка..." : "Проверить"}
					</Button>
				</div>

				{result && (
					<div className="rounded-md border p-3 mt-2 text-xs">
						<div className="flex items-center gap-2 mb-2">
							{result.feasible ? (
								<span className="text-green-600 font-semibold">✓ {t("ctp_feasible")}</span>
							) : (
								<span className="text-red-600 font-semibold">✗ {t("ctp_infeasible")}</span>
							)}
						</div>
						{result.earliestDeliveryDate && (
							<div>
								{t("ctp_earliest_date")}: <strong>{result.earliestDeliveryDate}</strong>
							</div>
						)}
						<div>
							{t("field_qty")}: {result.confirmedQty}
							{result.partialQty > 0 && ` (частично: ${result.partialQty})`}
						</div>
						<div>
							{t("field_status")}: {t(`ctp_constraint_${result.constraintType}`)}
						</div>
						{result.productionRequired && (
							<div>
								{t("ctp_production_required")}: {result.productionDays} {t("ctp_production_days")}
							</div>
						)}
						{result.explanation && (
							<div className="mt-1 text-muted-foreground">{result.explanation}</div>
						)}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}

// ── Menu bar ──────────────────────────────────────────────

function ManufacturingLayout() {
	const { t } = useTranslation("manufacturing");
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const replan = useTriggerReplan();
	const simulate = useSimulate();
	const { activeProductId } = useManufacturingContext();

	const [showNewDemand, setShowNewDemand] = useState(false);
	const [showCtp, setShowCtp] = useState(false);

	const handleReplan = useCallback(() => {
		if (!activeProductId) {
			toast.info("Выберите товар для перепланирования");
			return;
		}
		replan.mutate(
			{ productId: activeProductId },
			{
				onSuccess: (resp) => {
					toast.success(
						`Перепланирование завершено: ${resp.newActions.length} действий`,
					);
				},
			},
		);
	}, [activeProductId, replan]);

	const handleSimulate = useCallback(() => {
		if (!activeProductId) {
			toast.info("Выберите товар для симуляции");
			return;
		}
		simulate.mutate(
			{
				productId: activeProductId,
				demandMultiplier: 1.0,
				leadTimeMultiplier: 1.0,
				supplyDisruptionPct: 0.0,
				horizonDays: 90,
				monteCarloSamples: 1000,
			},
			{
				onSuccess: (resp) => {
					toast.success(
						`Симуляция: SL=${(resp.serviceLevel * 100).toFixed(0)}%, ` +
						`дефицит=${(resp.stockoutProbability * 100).toFixed(0)}%, ` +
						`${resp.scenariosRun} сценариев`,
					);
				},
			},
		);
	}, [activeProductId, simulate]);

	const handleRefresh = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["manufacturing"] });
		toast.success("Данные обновлены");
	}, [queryClient]);

	const handleExport = useCallback(() => {
		// AG Grid export is handled at the page level via gridRef
		toast.info("Используйте правый клик → Экспорт в таблице");
	}, []);

	const menuBar = useMemo<MenuBarItem[]>(
		() => [
			{
				id: "file",
				label: t("menu_file"),
				items: [
					{
						id: "new_demand",
						label: t("menu_new_demand"),
						shortcut: "Ctrl+N",
						onClick: () => setShowNewDemand(true),
					},
					{
						id: "new_po",
						label: t("menu_new_production_order"),
						onClick: () => navigate({ to: "/manufacturing/production" }),
					},
					{
						id: "new_bom",
						label: t("menu_new_bom"),
						onClick: () => {
							toast.info("Для создания спецификации перейдите в карточку товара → вкладка «Спецификации»");
							navigate({ to: "/manufacturing/board" });
						},
					},
					{ id: "sep1", label: "", separator: true },
					{
						id: "export",
						label: t("menu_export"),
						onClick: handleExport,
					},
				],
			},
			{
				id: "view",
				label: t("menu_view"),
				items: [
					{
						id: "refresh",
						label: t("menu_refresh"),
						shortcut: "F5",
						onClick: handleRefresh,
					},
					{ id: "sep2", label: "", separator: true },
					{
						id: "dashboard",
						label: t("dashboard"),
						onClick: () => navigate({ to: "/manufacturing" }),
					},
					{
						id: "exceptions",
						label: t("exceptions"),
						onClick: () => navigate({ to: "/manufacturing/exceptions" }),
					},
					{
						id: "board",
						label: t("board"),
						onClick: () => navigate({ to: "/manufacturing/board" }),
					},
					{
						id: "actions",
						label: t("actions"),
						onClick: () => navigate({ to: "/manufacturing/actions" }),
					},
				],
			},
			{
				id: "planning",
				label: t("menu_planning"),
				items: [
					{
						id: "replan",
						label: t("menu_replan"),
						shortcut: "Ctrl+Shift+R",
						onClick: handleReplan,
					},
					{
						id: "simulate",
						label: t("menu_simulate"),
						onClick: handleSimulate,
					},
					{
						id: "ctp",
						label: t("menu_ctp"),
						shortcut: "Ctrl+Shift+C",
						onClick: () => setShowCtp(true),
					},
				],
			},
			{
				id: "production",
				label: t("menu_production"),
				items: [
					{
						id: "orders",
						label: t("production"),
						onClick: () => navigate({ to: "/manufacturing/production" }),
					},
				],
			},
			{
				id: "sop",
				label: t("menu_sop"),
				items: [
					{
						id: "sop_view",
						label: t("sop"),
						onClick: () => navigate({ to: "/manufacturing/sop" }),
					},
				],
			},
		],
		[t, navigate, handleReplan, handleSimulate, handleRefresh, handleExport],
	);

	const config: WorkspaceConfig = useMemo(
		() => ({
			menuBar,
			statusBar: [],
		}),
		[menuBar],
	);

	return (
		<ModuleErrorBoundary>
			<ManufacturingWorkspaceInner config={config} />
			<NewDemandDialog open={showNewDemand} onOpenChange={setShowNewDemand} />
			<CtpDialog open={showCtp} onOpenChange={setShowCtp} />
		</ModuleErrorBoundary>
	);
}

function ManufacturingWorkspaceInner({
	config,
}: { config: WorkspaceConfig }) {
	const ctx = useWorkspaceContext();

	const mergedConfig = useMemo<WorkspaceConfig>(
		() => ({
			menuBar: config.menuBar,
			statusBar: ctx?.statusBar ?? config.statusBar,
		}),
		[config, ctx?.statusBar],
	);

	return (
		<Workspace config={mergedConfig}>
			<Outlet />
		</Workspace>
	);
}
